
import React, { useState } from "react";
import { useUserProfile } from "@/hooks/useUserProfile";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface ProgramViewerLayoutProps {
  programName: string;
  overview?: string | null;
  loading?: boolean;
  backButton?: React.ReactNode;
  children: React.ReactNode;
}

export function ProgramViewerLayout({
  programName,
  overview,
  loading = false,
  backButton,
  children
}: ProgramViewerLayoutProps) {
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(true);

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Sub-header with navigation and title at the far left */}
      <div className="w-full bg-black/30 backdrop-blur-sm border-b border-white/10 p-4">
        <div className="flex items-center justify-start w-full">
          {backButton}
          {loading ? (
            <Skeleton className="h-8 w-3/4 max-w-md bg-white/10 ml-2" />
          ) : (
            <h1 className="text-2xl font-bold text-white ml-2">
              {programName}
            </h1>
          )}
        </div>
      </div>
      
      {/* Fullscreen content area */}
      <div className="flex-1 overflow-auto px-6 py-6 space-y-6">
        {/* Program description section in its own container */}
        {loading ? (
          <div className="w-full bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-4">
            <Skeleton className="h-6 w-48 bg-white/10 mb-2" />
            <Skeleton className="h-20 w-full bg-white/10" />
          </div>
        ) : (
          <Collapsible 
            open={isDescriptionOpen} 
            onOpenChange={setIsDescriptionOpen}
            className="w-full bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-white/90">Program Description</h2>
              <CollapsibleTrigger className="text-white/70 hover:text-white transition-all focus:outline-none">
                {isDescriptionOpen ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="mt-2">
              {overview ? (
                <p className="text-white/70 whitespace-pre-wrap">{overview}</p>
              ) : (
                <p className="text-white/70">This program does not have a description.</p>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
        
        {/* Program content - now a direct child of the flex container */}
        {children}
      </div>
    </div>
  );
}
